import { ReactNode } from 'react';
import cn from 'classnames';
import MaskedInput from 'react-text-mask';

import Text from '@/components/common/Text/Text';
import { useGentleBlurField } from '@/components/form/lib/useGentleBlurField';

import './InputField.style.scss';

type MaskedFieldProps = {
  id?: string;
  name: string;
  /** Removes underscores per library: @url https://github.com/text-mask/text-mask/blob/master/componentDocumentation.md#guide */
  guide?: boolean;
  label: ReactNode;
  mask: Array<string | RegExp>;
  /** Post-processes the mask's conformed value against the raw input; see react-text-mask's `pipe`. */
  pipe?: (conformedValue: string, config: { rawValue: string }) => any;
  placeholder?: string;
  className?: string;
  type?: string;
  onFocus?: () => void;
  /** Optional HTML autocomplete token (e.g. "tel"). */
  autoComplete?: string;
  'data-testid'?: string;
};

function MaskedField(props: MaskedFieldProps) {
  const {
    id,
    name,
    guide,
    label,
    mask,
    pipe,
    className,
    placeholder,
    type = 'text',
    onFocus,
    autoComplete,
    'data-testid': dataTestId
  } = props;
  const [field, meta /*, helpers */] = useGentleBlurField({ name });

  return (
    // This component borrows styles from InputField.
    // To keep styling modularity, we will add the InputField class
    // along with importing its stylesheet for efficiency's sake
    <div className={cn('InputField', 'MaskedField')}>
      <label className='InputField-label'>{label}</label>
      <MaskedInput
        id={id}
        className={cn('InputField-input', className, {
          Error: meta.touched && meta.error
        })}
        {...field}
        guide={guide}
        type={type}
        autoComplete={autoComplete}
        mask={mask}
        pipe={pipe}
        placeholder={placeholder}
        onFocus={onFocus}
        data-testid={dataTestId}
      />
      {meta.touched && meta.error && (
        <div className='InputField-error'>
          <Text className='InputField-error-text'>{meta.error}</Text>
        </div>
      )}
    </div>
  );
}

export default MaskedField;
